import { extractErrorMessage } from "../utils/acceptSuggestions";
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import AISuggestionsBanner from "../components/AISuggestionsBanner";
import { persistAcceptedSuggestions, suggestionName } from "../utils/acceptSuggestions";
import StepDialog from "../components/StepDialog";
import ReadinessBadge from "../components/ReadinessBadge";
import { computeStallStatus, CURRENCY_BADGE } from "../utils/exerciseCurrency";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ClipboardCheck,
  Plus,
  Calendar,
  Users,
  Target,
  Clock,
  Play,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Lightbulb,
  TrendingUp,
  X,
  Download,
  Server,
  Building2,
  Link,
  ChevronRight,
  Sparkles,
  Pencil,
  Trash2,
} from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import AIRecommendationsButton from "../components/AIRecommendationsButton";

// Render a scheduled date without the UTC-midnight day-shift bug. Date-only
// strings ("2026-08-15") are parsed as a LOCAL date so users west of UTC see the
// same day the calendar cell uses; full ISO strings are rendered normally.
const formatExerciseDate = (val) => {
  if (!val) return "Not scheduled";
  const s = String(val);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString();
  const dt = new Date(s);
  return isNaN(dt.getTime()) ? s : dt.toLocaleDateString();
};


const EXERCISE_TYPES = [
  { value: "tabletop", label: "Tabletop", description: "Discussion-based scenario review" },
  { value: "walkthrough", label: "Walkthrough", description: "Step-by-step plan review" },
  { value: "simulation", label: "Simulation", description: "Simulated incident without disruption" },
  { value: "functional", label: "Functional", description: "Tests specific functions/teams" },
  { value: "full_scale", label: "Full Scale", description: "Organization-wide exercise" },
];

const STATUS_COLORS = {
  planned: "bg-blue-100 text-blue-700",
  in_progress: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-slate-100 text-slate-700",
};

export default function ExercisesPage() {
  const { api, user } = useAuth();
  const [exercises, setExercises] = useState([]);
  const [stats, setStats] = useState(null);
  const [exerciseFreq, setExerciseFreq] = useState({ months: 12, source: "default (12 months)" });
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showResultsDialog, setShowResultsDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [plansNeedingExercise, setPlansNeedingExercise] = useState(null);
  const [exerciseStep, setExerciseStep] = useState(0);
  // V14 D3 — AI conflict detection state
  const [conflictCheck, setConflictCheck] = useState(null);
  const [conflictChecking, setConflictChecking] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    exercise_type: "tabletop",
    scheduled_date: "",
    duration_hours: 2,
    objectives: "",
    scope: "",
    scenario_description: "",
    linked_plan_ids: [],
  });

  useEffect(() => {
    fetchData();
    fetchPlansData();
  }, []);

  const fetchData = async () => {
    try {
      const [exercisesRes, statsRes, lessonsRes, orgRes] = await Promise.all([
        api.get("/exercise-calendar/events"),
        api.get("/exercises/dashboard/stats"),
        api.get("/exercises/lessons-learned/all"),
        api.get("/organizations/current"),
      ]);
      setExercises(exercisesRes.data.events || []);
      setStats(statsRes.data);
      setLessons(lessonsRes.data.lessons || []);
      const org = orgRes.data || {};
      setExerciseFreq({
        months: org.exercise_frequency_months || org.exercise_frequency_recommended || 12,
        source: org.exercise_frequency_months
          ? "organization override"
          : (org.exercise_frequency_source || "default (12 months)"),
      });
    } catch (error) {
      console.error("Failed to fetch exercises:", error);
      toast.error("Failed to load exercises");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlansData = async () => {
    try {
      const [availableRes, needingRes] = await Promise.all([
        api.get("/exercises/plans/available"),
        api.get("/exercises/plans/needing-exercise"),
      ]);
      setAvailablePlans(availableRes.data.plans || []);
      setPlansNeedingExercise(needingRes.data);
    } catch (error) {
      console.error("Failed to fetch plans data:", error);
    }
  };

  // Accepted AI scenarios are created UNSCHEDULED. scheduled_date is MANDATORY in
  // the exercise model, so (per spec) we set a clearly-flagged placeholder 90 days
  // out in full ISO/UTC form — never today, never invented near-term. Status is the
  // backend default 'planned' (the create endpoint hardcodes it — weakest available).
  // Exercises have NO metadata field, so provenance is NOT written to any customer
  // prose field (description carries real scenario text instead).
  const handleAcceptScenarios = async (items) => {
    const placeholderDate = (() => {
      const d = new Date();
      d.setUTCDate(d.getUTCDate() + 90);
      d.setUTCHours(12, 0, 0, 0);
      return d.toISOString().replace(".000Z", "Z");
    })();
    const validTypes = ["tabletop", "walkthrough", "simulation", "functional", "full_scale"];
    const result = await persistAcceptedSuggestions({
      items,
      existingKeys: exercises.map((e) => e.title || e.name),
      keyOf: (raw) => suggestionName(raw),
      createOne: async (raw) => {
        const o = raw && typeof raw === "object" ? raw : {};
        const t = String(o.type || "").toLowerCase().replace(/\s+/g, "_");
        const exercise_type = validTypes.includes(t) ? t : "tabletop";
        const injects = Array.isArray(o.injects) ? o.injects : (o.injects ? [String(o.injects)] : []);
        const scenarioParts = [];
        if (injects.length) scenarioParts.push("Injects:\n" + injects.map((i) => `• ${i}`).join("\n"));
        if (o.scoring) scenarioParts.push(`Scoring criteria: ${o.scoring}`);
        await api.post("/exercises/", {
          name: suggestionName(raw),
          description: o.description || "",
          exercise_type,
          scheduled_date: placeholderDate,
          duration_hours: 2,
          objectives: Array.isArray(o.objectives) ? o.objectives : [],
          scope: "",
          assumptions: "Awaiting scheduling — placeholder date; to be set by the BCM manager.",
          scenario_description: scenarioParts.join("\n\n"),
          linked_plan_ids: [],
          custom_attributes: { source: "ai_suggestion" },
        });
      },
    });
    await fetchData();
    return result;
  };


  const resetExerciseForm = () => {
    setEditingId(null);
    setExerciseStep(0);
    setConflictCheck(null);
    setFormData({
      title: "",
      description: "",
      exercise_type: "tabletop",
      scheduled_date: "",
      duration_hours: 2,
      objectives: "",
      scope: "",
      scenario_description: "",
      linked_plan_ids: [],
    });
  };

  const openEditExercise = (exercise) => {
    setEditingId(exercise.id);
    setFormData({
      title: exercise.title || exercise.name || "",
      description: exercise.description || "",
      exercise_type: exercise.exercise_type || "tabletop",
      scheduled_date: (exercise.scheduled_date || "").slice(0, 16),
      duration_hours: exercise.duration_hours || 2,
      objectives: (exercise.objectives || []).join("\n"),
      scope: exercise.scope || "",
      scenario_description: exercise.scenario_description || "",
      linked_plan_ids: exercise.linked_plan_ids || [],
    });
    setExerciseStep(0);
    setShowCreateDialog(true);
  };

  const handleCreateExercise = async () => {
    try {
      const payload = {
        name: formData.title,
        description: formData.description,
        exercise_type: formData.exercise_type,
        scheduled_date: formData.scheduled_date,
        duration_hours: parseInt(formData.duration_hours),
        objectives: formData.objectives.split("\n").filter(o => o.trim()),
        scope: formData.scope,
        scenario_description: formData.scenario_description,
        linked_plan_ids: formData.linked_plan_ids,
      };
      if (editingId) {
        await api.put(`/exercises/${editingId}`, payload);
        toast.success("Exercise updated successfully");
      } else {
        await api.post("/exercises/", payload);
        toast.success("Exercise created successfully");
      }
      setShowCreateDialog(false);
      resetExerciseForm();
      fetchData();
      fetchPlansData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to save exercise"));
    }
  };

  const handleDeleteExercise = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/exercises/${deleteTarget.id}`);
      toast.success("Exercise deleted");
      setDeleteTarget(null);
      await fetchData();
      fetchPlansData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to delete exercise"));
    }
  };

  // V14 D3 — AI conflict detection for the proposed slot
  const handleCheckConflicts = async () => {
    if (!formData.scheduled_date) {
      toast.info("Pick a scheduled date first");
      return;
    }
    setConflictChecking(true);
    setConflictCheck(null);
    try {
      const r = await api.post("/ai-v14/exercise/detect-conflicts", {
        proposed_date: new Date(formData.scheduled_date).toISOString(),
        exercise_type: formData.exercise_type,
        duration_hours: parseInt(formData.duration_hours) || 2,
      });
      setConflictCheck(r.data || null);
      if ((r.data?.conflicts || []).length === 0) {
        toast.success("No calendar conflicts detected");
      } else {
        toast.warning(`${r.data.conflicts.length} conflict${r.data.conflicts.length === 1 ? "" : "s"} detected`);
      }
    } catch (e) {
      toast.error(extractErrorMessage(e, "AI conflict check failed"));
    } finally {
      setConflictChecking(false);
    }
  };

  const togglePlanSelection = (planId) => {
    setFormData(prev => ({
      ...prev,
      linked_plan_ids: prev.linked_plan_ids.includes(planId)
        ? prev.linked_plan_ids.filter(id => id !== planId)
        : [...prev.linked_plan_ids, planId]
    }));
  };

  const handleUpdateStatus = async (exerciseId, newStatus) => {
    try {
      await api.put(`/exercises/${exerciseId}/status?status=${newStatus}`);
      toast.success(`Exercise status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const openResultsDialog = (exercise) => {
    setSelectedExercise(exercise);
    setShowResultsDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-sky-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="exercises-page">
      <AISuggestionsBanner module="exercise_scenarios" onAccept={handleAcceptScenarios} />
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-amber-100 text-amber-700">CHECK Phase</Badge>
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">ISO 22301:8.5</Badge>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Exercises & Validation</h1>
          <p className="text-slate-600 mt-1">
            Test and validate your BCM plans through structured exercises.{" "}
            <a href="/exercise-test-review" className="text-amber-700 hover:underline font-medium" data-testid="exercise-test-review-link">
              Exercise Test Review →
            </a>
          </p>
          <div className="mt-2">
            <ReadinessBadge
              total={exercises?.length || 0}
              complete={exercises?.filter(e => e.status === 'completed')?.length || 0}
              label="Exercises"
              completeLabel="completed"
            />
          </div>
        </div>
        {user?.role === "bcm_manager" && (
          <div className="flex gap-2">
            <AIRecommendationsButton 
              module="exercise" 
              contextData={{
                total_exercises: exercises?.length || 0,
                completed: exercises?.filter(e => e.status === 'completed')?.length || 0,
                planned: exercises?.filter(e => e.status === 'planned')?.length || 0
              }}
              api={api}
            />
            <Button data-testid="create-exercise-btn" onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Schedule Exercise
            </Button>
          </div>
        )}
      </div>

      {/* Create Exercise Dialog — TurboTax Progressive Disclosure */}
      <StepDialog
        open={showCreateDialog}
        onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetExerciseForm(); }}
        title={editingId ? "Edit Exercise" : "Schedule New Exercise"}
        currentStep={exerciseStep}
        onStepChange={setExerciseStep}
        onSubmit={handleCreateExercise}
        onCancel={() => { setShowCreateDialog(false); resetExerciseForm(); }}
        submitLabel={editingId ? "Save Changes" : "Create Exercise"}
        submitDisabled={!formData.title || !formData.scheduled_date}
        canAdvance={
          exerciseStep === 0 ? !!formData.title :
          exerciseStep === 1 ? !!formData.scheduled_date :
          true
        }
        steps={[
          { id: "basics", title: "Basics", subtitle: "What is this exercise and what type will it be?", isComplete: !!formData.title && !!formData.exercise_type },
          { id: "schedule", title: "Schedule", subtitle: "When will this exercise take place and what is its scope?", isComplete: !!formData.scheduled_date },
          { id: "scenario", title: "Scenario", subtitle: "Describe the scenario, objectives, and what you aim to validate.", isComplete: !!formData.objectives || !!formData.scenario_description },
          { id: "plans", title: "Link Plans", subtitle: "Which recovery plans should be tested during this exercise?", isComplete: formData.linked_plan_ids.length > 0 },
        ]}
      >
        {/* Step 0: Basics */}
        {exerciseStep === 0 && (
          <div className="space-y-4" data-testid="step-basics">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Exercise Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., Annual Tabletop - Cyber Incident"
                data-testid="exercise-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Exercise Type</Label>
              <div className="space-y-2">
                {EXERCISE_TYPES.map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData({...formData, exercise_type: type.value})}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      formData.exercise_type === type.value
                        ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`exercise-type-${type.value}`}
                  >
                    <p className="font-medium text-sm">{type.label}</p>
                    <p className="text-xs text-slate-500">{type.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Schedule */}
        {exerciseStep === 1 && (
          <div className="space-y-4" data-testid="step-schedule">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Scheduled Date *</Label>
              <Input
                type="datetime-local"
                value={formData.scheduled_date}
                onChange={(e) => { setFormData({...formData, scheduled_date: e.target.value}); setConflictCheck(null); }}
                data-testid="scheduled-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Duration (hours)</Label>
              <Input
                type="number"
                min="1"
                max="8"
                value={formData.duration_hours}
                onChange={(e) => setFormData({...formData, duration_hours: e.target.value})}
                data-testid="duration-input"
              />
            </div>

            {/* V14 D3 — AI Conflict Detector */}
            <div className="border border-purple-200 bg-purple-50/40 rounded-lg p-3 space-y-2" data-testid="ai-conflict-panel">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-md bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-bold">AI</span>
                  <div>
                    <p className="text-xs font-semibold text-slate-800">Calendar Conflict Detector</p>
                    <p className="text-[11px] text-slate-500">Checks ±3 days for double-booking, fatigue, change freezes</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleCheckConflicts}
                  disabled={conflictChecking || !formData.scheduled_date}
                  className="px-3 py-1.5 rounded-md border border-purple-300 bg-white text-purple-700 text-xs font-semibold hover:bg-purple-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="check-conflicts-btn"
                >
                  {conflictChecking ? "Checking…" : "Check for Conflicts"}
                </button>
              </div>
              {conflictCheck && (
                <div className="text-xs space-y-1.5 pt-1">
                  {(conflictCheck.conflicts || []).length === 0 ? (
                    <p className="text-emerald-700 font-medium">✓ No conflicts detected for this slot.</p>
                  ) : (
                    <>
                      <p className="text-rose-700 font-semibold">
                        {(conflictCheck.conflicts || []).length} conflict{(conflictCheck.conflicts || []).length === 1 ? "" : "s"}:
                      </p>
                      {(conflictCheck.conflicts || []).map((c, i) => (
                        <div key={i} className="pl-3 border-l-2 border-rose-300 text-slate-700">
                          <span className="font-semibold">{c.conflict_type?.replace(/_/g, " ")}</span>
                          {c.severity && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-rose-100 text-rose-700">{c.severity}</span>}
                          <p className="text-slate-600 text-[11px] mt-0.5">{c.reason}</p>
                        </div>
                      ))}
                    </>
                  )}
                  {(conflictCheck.suggested_slots || []).length > 0 && (
                    <div className="pt-1.5">
                      <p className="text-[11px] font-semibold text-slate-600 mb-1">AI-suggested alternate slots:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {(conflictCheck.suggested_slots || []).slice(0, 4).map((s, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => {
                              // Convert ISO back to datetime-local format
                              const d = new Date(s.slot);
                              const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
                              setFormData({ ...formData, scheduled_date: local });
                              setConflictCheck(null);
                              toast.success("Slot applied");
                            }}
                            className="text-[11px] px-2 py-1 rounded border border-slate-300 bg-white hover:border-purple-400 hover:bg-purple-50"
                          >
                            {new Date(s.slot).toLocaleDateString()} <span className="text-slate-400">•</span> {s.rationale}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Scope</Label>
              <Input
                value={formData.scope}
                onChange={(e) => setFormData({...formData, scope: e.target.value})}
                placeholder="e.g., IT Department and CMT"
                data-testid="scope-input"
              />
            </div>
          </div>
        )}

        {/* Step 2: Scenario */}
        {exerciseStep === 2 && (
          <div className="space-y-4" data-testid="step-scenario">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of the exercise"
                rows={2}
                data-testid="description-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Objectives (one per line)</Label>
              <Textarea
                value={formData.objectives}
                onChange={(e) => setFormData({...formData, objectives: e.target.value})}
                placeholder={"Test communication protocols\nValidate decision-making process\nIdentify gaps in procedures"}
                rows={3}
                data-testid="objectives-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Scenario Description</Label>
              <Textarea
                value={formData.scenario_description}
                onChange={(e) => setFormData({...formData, scenario_description: e.target.value})}
                placeholder="Describe the scenario to be simulated..."
                rows={3}
                data-testid="scenario-input"
              />
            </div>
          </div>
        )}

        {/* Step 3: Link Plans */}
        {exerciseStep === 3 && (
          <div className="space-y-4" data-testid="step-plans">
            <Label className="flex items-center gap-2 text-sm font-semibold">
              <Link className="w-4 h-4 text-sky-700" />
              Link Recovery Plans to Test
            </Label>
            {availablePlans.length === 0 ? (
              <div className="text-sm text-slate-500 p-4 bg-slate-50 rounded-lg text-center">
                No approved recovery plans available. Create and approve BCP/DRP plans first.
              </div>
            ) : (
              <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-2">
                {availablePlans.map(plan => {
                  const needsExercise = plansNeedingExercise?.needing_exercise?.some(p => p.id === plan.id);
                  return (
                    <div 
                      key={plan.id}
                      className={`p-2 border rounded cursor-pointer transition-all ${
                        formData.linked_plan_ids.includes(plan.id) 
                          ? 'border-sky-700 bg-sky-50' 
                          : needsExercise 
                            ? 'border-amber-300 bg-amber-50 hover:border-sky-300'
                            : 'border-slate-200 hover:border-sky-300'
                      }`}
                      onClick={() => togglePlanSelection(plan.id)}
                      data-testid={`plan-select-${plan.id}`}
                    >
                      <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                        <Checkbox 
                          checked={formData.linked_plan_ids.includes(plan.id)}
                          onCheckedChange={() => togglePlanSelection(plan.id)}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            {plan.plan_type === 'bcp' ? (
                              <Building2 className="w-4 h-4 text-blue-600" />
                            ) : (
                              <Server className="w-4 h-4 text-purple-600" />
                            )}
                            <span className="font-medium text-sm">{plan.title}</span>
                            <span className={`px-1.5 py-0.5 text-xs rounded ${
                              plan.plan_type === 'bcp' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                            }`}>
                              {plan.plan_type?.toUpperCase()}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                            {plan.business_unit && <span>{plan.business_unit}</span>}
                            {plan.last_exercised_at ? (
                              <span className={plan.last_exercise_passed ? 'text-emerald-600' : 'text-red-600'}>
                                Last tested: {new Date(plan.last_exercised_at).toLocaleDateString()} 
                                {plan.last_exercise_passed ? ' ✓' : ' ✗'}
                              </span>
                            ) : (
                              <span className="text-amber-600">Never exercised</span>
                            )}
                            {needsExercise && (
                              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">
                                Needs Exercise
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {formData.linked_plan_ids.length > 0 && (
              <p className="text-xs text-sky-700 mt-2">
                {formData.linked_plan_ids.length} plan(s) selected for testing
              </p>
            )}
          </div>
        )}
      </StepDialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent data-testid="delete-exercise-dialog">
          <DialogHeader>
            <DialogTitle>Delete exercise?</DialogTitle>
            <DialogDescription>
              This permanently deletes <span className="font-semibold text-slate-900">{deleteTarget?.title}</span>. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="cancel-delete-exercise">Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteExercise} data-testid="confirm-delete-exercise">
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Exercises</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_exercises ?? 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-sky-100 flex items-center justify-center">
                  <ClipboardCheck className="w-6 h-6 text-sky-700" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="exercise-overdue-tile">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Overdue</p>
                  <p className="text-2xl font-bold text-red-600" data-testid="exercise-overdue-count">
                    {(() => {
                      const completed = exercises.filter(e => e.status === "completed" && e.completed_date);
                      const overdue = completed.filter(e => computeStallStatus(e.completed_date, exerciseFreq.months).effective_status === "overdue").length;
                      return overdue + (completed.length === 0 && exercises.length > 0 ? 1 : 0);
                    })()}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Planned</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.by_status?.planned ?? 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Completed</p>
                  <p className="text-2xl font-bold text-emerald-600">{stats.completed ?? 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Open Lessons</p>
                  <p className="text-2xl font-bold text-amber-600" data-testid="open-lessons-count">{lessons.filter(l => String(l.status || '').toLowerCase() !== 'closed').length}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Lightbulb className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Plans Needing Exercise - ISO 22301 Compliance */}
      {plansNeedingExercise && plansNeedingExercise.needing_exercise?.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Plans Requiring Exercise
              <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                {plansNeedingExercise.needing_exercise.length} plans
              </Badge>
            </CardTitle>
            <CardDescription className="text-amber-700">
              Based on your BCM Policy ({plansNeedingExercise.policy_settings?.threshold_days || 180} day threshold)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {plansNeedingExercise.needing_exercise.slice(0, 6).map(plan => (
                <div key={plan.id} className="p-3 bg-white rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2 mb-1">
                    {plan.plan_type === 'bcp' ? (
                      <Building2 className="w-4 h-4 text-blue-600" />
                    ) : (
                      <Server className="w-4 h-4 text-purple-600" />
                    )}
                    <span className="font-medium text-sm truncate">{plan.title}</span>
                  </div>
                  <p className="text-xs text-amber-700">{plan.reason}</p>
                  {plan.business_unit && (
                    <p className="text-xs text-slate-500 mt-1">{plan.business_unit}</p>
                  )}
                </div>
              ))}
            </div>
            {plansNeedingExercise.needing_exercise.length > 6 && (
              <p className="text-sm text-amber-700 mt-3">
                +{plansNeedingExercise.needing_exercise.length - 6} more plans need exercise
              </p>
            )}
            <div className="mt-4">
              <Button 
                onClick={() => setShowCreateDialog(true)}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="schedule-exercise-from-warning"
              >
                <Plus className="w-4 h-4 mr-2" />
                Schedule Exercise for These Plans
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Exercise Types by BCI GPG */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-sky-700" />
            BCI GPG Exercise Types
          </CardTitle>
          <CardDescription>Choose the appropriate exercise type based on your objectives</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {EXERCISE_TYPES.map(type => (
              <div key={type.value} className="p-4 rounded-lg border bg-slate-50 text-center">
                <p className="font-semibold text-slate-900">{type.label}</p>
                <p className="text-xs text-slate-500 mt-1">{type.description}</p>
                <p className="text-lg font-bold text-sky-700 mt-2">{stats?.by_type?.[type.value] || 0}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Exercises List */}
      <Card>
        <CardHeader>
          <CardTitle>Exercise Schedule</CardTitle>
        </CardHeader>
        <CardContent>
          {exercises.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ClipboardCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No exercises scheduled yet</p>
              <p className="text-sm mt-1">Schedule your first exercise to validate your BCM plans</p>
            </div>
          ) : (
            <div className="space-y-4">
              {exercises.map(exercise => (
                <div key={exercise.id} className="p-4 border rounded-lg hover:border-sky-200 transition-colors" data-testid={`exercise-${exercise.id}`}>
                  <div className="flex flex-col lg:flex-row justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-slate-900">{exercise.title}</h3>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_COLORS[exercise.status]}`}>
                          {exercise.status.replace("_", " ")}
                        </span>
                        <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-600">
                          {EXERCISE_TYPES.find(t => t.value === exercise.exercise_type)?.label}
                        </span>
                        {(() => {
                          const cur = computeStallStatus(exercise.status === "completed" ? exercise.completed_date : null, exerciseFreq.months);
                          const b = CURRENCY_BADGE[cur.effective_status];
                          return (
                            <span className={`px-2 py-0.5 text-xs rounded-full ${b.className}`} data-testid={`exercise-currency-badge-${exercise.id}`}>
                              {b.label}{cur.effective_status === "overdue" ? ` · ${cur.days_overdue} days overdue` : ""}
                            </span>
                          );
                        })()}
                        {exercise.custom_attributes?.source === "ai_suggestion" && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700 border border-purple-200" data-testid={`ai-suggested-badge-${exercise.id}`}>
                            <Sparkles className="w-3 h-3" /> AI-suggested
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mb-2">{exercise.description}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatExerciseDate(exercise.scheduled_date)}
                        </span>
                        {exercise.assumptions && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[11px] border border-slate-200" data-testid={`placeholder-date-note-${exercise.id}`} title={exercise.assumptions}>
                            <Clock className="w-3 h-3" /> Placeholder date — not scheduled
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {exercise.duration_hours}h
                        </span>
                        {exercise.scope && (
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {exercise.scope}
                          </span>
                        )}
                      </div>
                      {(() => {
                        const cur = computeStallStatus(exercise.status === "completed" ? exercise.completed_date : null, exerciseFreq.months);
                        return (
                          <div className="mt-2 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1" data-testid={`exercise-currency-detail-${exercise.id}`}>
                            <span>Required: every {exerciseFreq.months} months · {exerciseFreq.source}</span>
                            {cur.effective_status === "overdue" && <span className="text-red-600 font-medium">{cur.days_overdue} days overdue</span>}
                            {cur.days_remaining != null && <span>{cur.days_remaining} days remaining</span>}
                            {cur.deadline && <span>Next deadline: {new Date(cur.deadline).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}</span>}
                          </div>
                        );
                      })()}
                      {exercise.objectives?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs font-medium text-slate-500 mb-1">Objectives:</p>
                          <ul className="text-xs text-slate-600 list-disc list-inside">
                            {exercise.objectives.slice(0, 3).map((obj, i) => (
                              <li key={i}>{obj}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {exercise.scenario_description && (
                        <details className="mt-2 group" data-testid={`scenario-detail-${exercise.id}`}>
                          <summary className="text-xs font-medium text-slate-500 cursor-pointer hover:text-slate-700 list-none flex items-center gap-1">
                            <ChevronRight className="w-3 h-3 transition-transform group-open:rotate-90" /> Scenario, injects &amp; scoring
                          </summary>
                          <p className="text-xs text-slate-600 mt-1 whitespace-pre-line pl-4">{exercise.scenario_description}</p>
                        </details>
                      )}
                    </div>
                    {(exercise.source || "exercises") === "exercises" ? (
                      user?.role === "bcm_manager" && (
                      <div className="flex lg:flex-col gap-2">
                        {exercise.status === "planned" && (
                          <Button size="sm" variant="outline" onClick={() => handleUpdateStatus(exercise.id, "in_progress")}>
                            <Play className="w-4 h-4 mr-1" /> Start
                          </Button>
                        )}
                        {exercise.status === "in_progress" && (
                          <Button size="sm" variant="outline" onClick={() => openResultsDialog(exercise)}>
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Complete
                          </Button>
                        )}
                        {exercise.status === "completed" && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/export/exercise/${exercise.id}/pdf`, '_blank')}
                            data-testid={`export-exercise-${exercise.id}`}
                          >
                            <FileText className="w-4 h-4 mr-1" /> Report
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => openEditExercise(exercise)} data-testid={`edit-exercise-${exercise.id}`}>
                          <Pencil className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        {exercise.status === "planned" && (
                          <Button size="sm" variant="ghost" onClick={() => handleUpdateStatus(exercise.id, "cancelled")}>
                            <X className="w-4 h-4 mr-1" /> Cancel
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setDeleteTarget(exercise)} data-testid={`delete-exercise-${exercise.id}`}>
                          <Trash2 className="w-4 h-4 mr-1" /> Delete
                        </Button>
                      </div>
                      )
                    ) : (
                      <div className="flex lg:flex-col items-start gap-1">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full bg-sky-50 text-sky-700 border border-sky-200" data-testid={`calendar-source-${exercise.id}`} title="Scheduled from the Exercise Calendar">
                          <Calendar className="w-3 h-3" /> Scheduled via Calendar
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lessons Learned */}
      {lessons.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-600" />
                Lessons Learned
                <span className="ml-2 text-xs font-normal px-2 py-0.5 bg-amber-100 text-amber-700 rounded">ISO 8.5</span>
              </CardTitle>
            </div>
            <CardDescription>
              Findings from exercises that may require corrective or preventive actions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {lessons.slice(0, 5).map(lesson => {
                const sev = String(lesson.impact_level || "").toLowerCase();
                const status = String(lesson.status || "open").toLowerCase();
                return (
                <div key={lesson.id} className="p-3 border rounded-lg bg-amber-50 border-amber-100" data-testid={`lesson-${lesson.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900">{lesson.title || lesson.description}</p>
                      {lesson.description && lesson.description !== lesson.title && (
                        <p className="text-sm text-slate-600 mt-1">{lesson.description}</p>
                      )}
                      {lesson.recommendation && (
                        <p className="text-sm text-slate-500 mt-1"><span className="font-medium">Recommendation:</span> {lesson.recommendation}</p>
                      )}
                      {lesson.source_name && (
                        <p className="text-xs text-slate-400 mt-1">From exercise: {lesson.source_name}</p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-600 capitalize">{status.replace("_", " ")}</span>
                        <span className={`px-2 py-0.5 text-xs rounded-full capitalize ${
                          sev === "high" || sev === "critical" ? "bg-red-100 text-red-700" :
                          sev === "medium" ? "bg-amber-100 text-amber-700" :
                          "bg-slate-100 text-slate-700"
                        }`}>
                          {sev || "—"}
                        </span>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-xs"
                        onClick={() => {
                          const pacaData = encodeURIComponent(JSON.stringify({
                            title: lesson.title || lesson.description,
                            source: `Exercise: ${lesson.source_name || 'Unknown'}`,
                            description: lesson.recommendation || lesson.description,
                            severity: sev === 'high' || sev === 'critical' ? 'critical' : sev === 'medium' ? 'major' : 'minor',
                          }));
                          window.location.href = `/capa?prefill=${pacaData}`;
                        }}
                      >
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Create PACA
                      </Button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
